
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Eye, Clock, BarChart2, MessageSquare, Share2, ThumbsUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface ArticlePerformanceProps {
  timeframe: "7d" | "30d" | "90d" | "1y";
}

// Sample article data
const articles = [
  {
    id: 1,
    title: "The Future of Renewable Energy in Urban Environments",
    date: "2023-06-15",
    views: 3254,
    readTime: "4m 12s",
    completionRate: 78,
    comments: 42,
    shares: 128,
    likes: 256
  },
  {
    id: 2,
    title: "Artificial Intelligence: Transforming Healthcare",
    date: "2023-07-02",
    views: 2845,
    readTime: "5m 47s",
    completionRate: 82,
    comments: 37,
    shares: 95,
    likes: 312
  },
  {
    id: 3,
    title: "Climate Change: The Economic Impact",
    date: "2023-07-18",
    views: 4210,
    readTime: "6m 23s",
    completionRate: 71,
    comments: 63,
    shares: 187,
    likes: 423
  },
  {
    id: 4,
    title: "The Rise of Digital Nomads in Post-Pandemic Era",
    date: "2023-08-05",
    views: 1987,
    readTime: "3m 59s",
    completionRate: 65,
    comments: 28,
    shares: 76,
    likes: 154
  },
  {
    id: 5,
    title: "Cryptocurrency Regulations: A Global Perspective",
    date: "2023-08-21",
    views: 3678,
    readTime: "5m 11s",
    completionRate: 73,
    comments: 52,
    shares: 104,
    likes: 278
  },
  {
    id: 6,
    title: "Mental Health in the Digital Age",
    date: "2023-09-08",
    views: 5123,
    readTime: "4m 42s",
    completionRate: 88,
    comments: 76,
    shares: 263,
    likes: 542
  },
  {
    id: 7,
    title: "Sustainable Fashion: Trends and Challenges",
    date: "2023-09-27",
    views: 2456,
    readTime: "3m 36s",
    completionRate: 69,
    comments: 31,
    shares: 82,
    likes: 197
  },
];

const ArticlePerformance: React.FC<ArticlePerformanceProps> = ({ timeframe }) => {
  const [sortBy, setSortBy] = useState<string>("views");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Filter and sort articles
  const filteredArticles = articles
    .filter(article => 
      article.title.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const aValue = a[sortBy as keyof typeof a];
      const bValue = b[sortBy as keyof typeof b];
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // For string values
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc' 
          ? aValue.localeCompare(bValue) 
          : bValue.localeCompare(aValue);
      }
      
      return 0;
    });

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  return (
    <Card className="dark:bg-gray-800 dark:text-gray-100">
      <CardHeader>
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
          <CardTitle>Article Performance</CardTitle>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative">
              <Input
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full sm:w-[200px]"
              />
            </div>
            <Select defaultValue="views" onValueChange={(value) => handleSort(value)}>
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="views">Views</SelectItem>
                <SelectItem value="completionRate">Completion Rate</SelectItem>
                <SelectItem value="comments">Comments</SelectItem>
                <SelectItem value="shares">Shares</SelectItem>
                <SelectItem value="likes">Likes</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[300px]">Title</TableHead>
              <TableHead className="text-right">
                <div className="flex items-center justify-end gap-1 cursor-pointer" onClick={() => handleSort('views')}>
                  <Eye className="h-4 w-4" />
                  <span>Views</span>
                </div>
              </TableHead>
              <TableHead className="text-right">
                <div className="flex items-center justify-end gap-1 cursor-pointer" onClick={() => handleSort('readTime')}>
                  <Clock className="h-4 w-4" />
                  <span>Read Time</span>
                </div>
              </TableHead>
              <TableHead className="text-right">
                <div className="flex items-center justify-end gap-1 cursor-pointer" onClick={() => handleSort('completionRate')}>
                  <BarChart2 className="h-4 w-4" />
                  <span>Completion</span>
                </div>
              </TableHead>
              <TableHead className="text-right">
                <div className="flex items-center justify-end gap-1 cursor-pointer" onClick={() => handleSort('comments')}>
                  <MessageSquare className="h-4 w-4" />
                  <span>Comments</span>
                </div>
              </TableHead>
              <TableHead className="text-right">
                <div className="flex items-center justify-end gap-1 cursor-pointer" onClick={() => handleSort('shares')}>
                  <Share2 className="h-4 w-4" />
                  <span>Shares</span>
                </div>
              </TableHead>
              <TableHead className="text-right">
                <div className="flex items-center justify-end gap-1 cursor-pointer" onClick={() => handleSort('likes')}>
                  <ThumbsUp className="h-4 w-4" />
                  <span>Likes</span>
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredArticles.map((article) => (
              <TableRow key={article.id}>
                <TableCell className="font-medium">{article.title}</TableCell>
                <TableCell className="text-right">{article.views.toLocaleString()}</TableCell>
                <TableCell className="text-right">{article.readTime}</TableCell>
                <TableCell className="text-right">{article.completionRate}%</TableCell>
                <TableCell className="text-right">{article.comments}</TableCell>
                <TableCell className="text-right">{article.shares}</TableCell>
                <TableCell className="text-right">{article.likes}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex justify-end mt-4">
          <Button variant="outline">Export Data</Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ArticlePerformance;
